import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { map, Observable } from 'rxjs';

import {
	DeviceUiSettingsFactoryResetBehaviorPlugin,
	FactoryResetText
} from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

/**
 * Provides a default device behavior implementation, including only the settings page of the SDC.
 */
@Injectable()
@shUseTranslation('scCdmSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class DefaultCdmDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [];

	constructor(private readonly transloco: TranslocoService) {}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getDefaultRoute(): DeviceUiRoute {
		return DeviceUiRoute.Settings;
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?:
			| {
					virtual?: boolean | undefined;
					autoCoverageMode?: boolean | undefined;
					dspSettingsPageEnabled?: boolean;
			  }
			| undefined
	): Observable<DeviceUiPage[] | undefined> {
		return this.transloco.selectTranslate('scCdmSharedDevBehavior.toolbar.labels.settings').pipe(
			map((settingsLabel) => {
				return [
					{
						route: DeviceUiRoute.Settings,
						label: settingsLabel
					}
				];
			})
		);
	}

	public showPatentInfo(): boolean {
		return true;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scCdmSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scCdmSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scCdmSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedWithoutImage;
	}

	public hideLinkAllButton(): boolean {
		return true;
	}
}
